// Builds fal-style sample code (curl / javascript / python) for the generate
// API, mirroring exactly what GenerationPanel is about to submit. The three
// snippets always describe the same three-step flow:
//   1. POST the body to /v1/generate/:model_id            -> { id, status }
//   2. GET .../requests/:id/status, polled until "completed"/"failed"
//   3. GET .../requests/:id for the result JSON - media items carry a
//      content_url path, fetched with the same Authorization header, AND a
//      sibling "url" - an absolute, signed, public link that needs no auth
//      header and expires after 7 days - handy for quick sharing.

export interface GenerationSnippetInput {
  modelId: string;
  displayName: string;
  body: Record<string, unknown>;
  // env.INFERENCE_API_URL, already trimmed of its trailing slash.
  baseUrl: string;
}

export interface GenerationSnippets {
  curl: string;
  javascript: string;
  python: string;
}

// Escapes a string for safe embedding inside a single-quoted bash argument:
// bash has no in-quote escape character, so the only way to embed a literal
// `'` is to close the quote, emit an escaped literal apostrophe, and reopen
// the quote - the standard `'\''` unit.
function shellSingleQuoteEscape(s: string): string {
  return s.split("'").join("'\\''");
}

// Escapes a string for safe embedding inside a Python raw triple-single-quoted
// string ( r'''...''' ) that will itself be parsed as JSON via json.loads.
// Raw strings disable backslash-escape processing, which is exactly what we
// want here - it lets `\"` and `\\` from JSON.stringify reach json.loads
// unprocessed instead of being corrupted by Python's normal string escaping
// first. The one thing a raw string *can't* do is embed its own delimiter:
// a literal `'` in the body (vanishingly rare, but possible via prompt text)
// could prematurely close the literal, or a run of three could collide with
// the closing ''' outright. Since JSON's grammar never places a bare `'`
// anywhere except inside a string's content, it's always safe to replace
// every occurrence with the equivalent JSON unicode escape ' - it stays
// valid JSON (json.loads decodes ' back to `'`) and eliminates the
// character entirely, so no `'''` collision can occur.
function pythonRawTripleQuoteEscape(s: string): string {
  return s.split("'").join('\\u0027');
}

export function buildGenerationSnippets({
  modelId,
  displayName,
  body,
  baseUrl,
}: GenerationSnippetInput): GenerationSnippets {
  const trimmedBase = baseUrl.replace(/\/$/, '');
  const encodedModel = encodeURIComponent(modelId);
  const genPath = `${trimmedBase}/v1/generate/${encodedModel}`;
  const bodyJson = JSON.stringify(body, null, 2);

  const curl = `# 1. Submit a generation request for ${displayName}
curl -X POST "${genPath}" \\
  -H "Authorization: Bearer $PODSTACK_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '${shellSingleQuoteEscape(bodyJson)}'
# -> { "id": "REQUEST_ID", "status": "queued" }

# 2. Poll status until it reports "completed" (or "failed") - status is one
#    of: queued, in_progress, completed, failed
curl "${genPath}/requests/REQUEST_ID/status" \\
  -H "Authorization: Bearer $PODSTACK_API_KEY"
# -> { "id": "REQUEST_ID", "status": "in_progress", "queue_position": 2 }

# 3. Once completed, fetch the result
curl "${genPath}/requests/REQUEST_ID" \\
  -H "Authorization: Bearer $PODSTACK_API_KEY"
# -> result JSON; media items carry a "content_url" path - fetch that with
#    the same Authorization header to download the generated file(s). Each
#    item also carries a "url" - an absolute, signed, public link (no auth
#    header needed) that expires after 7 days - handy for quick sharing.`;

  const javascript = `// Sample code for ${displayName} - submit, poll, then fetch the result.
const PODSTACK_API_KEY = process.env.PODSTACK_API_KEY;

async function generate() {
  // 1. Submit the generation request
  const submitResp = await fetch("${genPath}", {
    method: "POST",
    headers: {
      Authorization: "Bearer " + PODSTACK_API_KEY,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(${bodyJson}),
  });
  const { id } = await submitResp.json();

  // 2. Poll every 2s until the job completes (or fails)
  let job;
  while (true) {
    const statusResp = await fetch("${genPath}/requests/" + id + "/status", {
      headers: { Authorization: "Bearer " + PODSTACK_API_KEY },
    });
    job = await statusResp.json();
    if (job.status === "completed" || job.status === "failed") break;
    await new Promise((resolve) => setTimeout(resolve, 2000));
  }
  if (job.status === "failed") throw new Error("Generation failed");

  // 3. Fetch the result
  const resultResp = await fetch("${genPath}/requests/" + id, {
    headers: { Authorization: "Bearer " + PODSTACK_API_KEY },
  });
  const result = await resultResp.json();

  // Media items carry a content_url - download them with the same
  // Authorization header used above. Each item also carries a "url": an
  // absolute, signed, public link (no auth header needed) that expires
  // after 7 days - handy for quick sharing.
  console.log(result);
  return result;
}

generate();`;

  const python = `# Sample code for ${displayName} - submit, poll, then fetch the result.
import json
import os
import time

import requests

PODSTACK_API_KEY = os.environ["PODSTACK_API_KEY"]
headers = {"Authorization": "Bearer " + PODSTACK_API_KEY}

# 1. Submit the generation request
# (json.loads keeps this a straight embed of the real JSON body - true/
# false/null parse correctly without any true->True conversion. The r'''...'''
# raw string is required so Python doesn't pre-process \\" / \\\\ escapes
# before json.loads ever sees them.)
payload = json.loads(r'''${pythonRawTripleQuoteEscape(bodyJson)}''')
resp = requests.post(
    "${genPath}",
    headers={**headers, "Content-Type": "application/json"},
    json=payload,
)
job = resp.json()
request_id = job["id"]

# 2. Poll every 2s until the job completes (or fails)
while True:
    status_resp = requests.get(
        "${genPath}/requests/" + request_id + "/status",
        headers=headers,
    )
    job = status_resp.json()
    if job["status"] in ("completed", "failed"):
        break
    time.sleep(2)

if job["status"] == "failed":
    raise RuntimeError("Generation failed")

# 3. Fetch the result
result_resp = requests.get(
    "${genPath}/requests/" + request_id,
    headers=headers,
)
result = result_resp.json()

# Media items carry a content_url - download them with the same
# Authorization header used above. Each item also carries a "url": an
# absolute, signed, public link (no auth header needed) that expires after
# 7 days - handy for quick sharing.
print(result)`;

  return { curl, javascript, python };
}
